import React from "react";
import { DateTime } from "luxon";
import AutoComplete from "../../utils/components/AutoCompleteComp/AutoComplete2.tsx";
import DateInput from "../../utils/components/DateInput.tsx";
import FloatInput from "../../utils/components/FloatInput.tsx";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import { filterCompte } from "../processing/compteSearcher";
import modes from "../../main/consts/modes.ts";
import type {
  FactureDuplicateCandidate,
  ImportedInvoiceAnalysis,
  ImportedDoc,
  FactureDraft,
} from "compta-shared";
import DuplicateFactureWarningModal, { getDuplicateConfirmationKeys } from "./DuplicateFactureWarningModal.tsx";
import ConfirmModal from "./ConfirmModal.tsx";
import {
  getImportedInvoiceReviewInitialValues,
  shouldConfirmImportedInvoiceAnalysis,
} from "./importedInvoiceReviewState.ts";

type InvoiceType = "normal" | "resto";
type InvoiceMode = (typeof modes)[number];

type ImportedInboxItem = {
  _id: string;
  uploadedAt: Date;
  originalFileName: string;
  invoiceToPay?: boolean;
  pdfUrl: string;
  sourceEmailSender?: string | null;
  sourceEmailSubject?: string | null;
  analysis?: ImportedInvoiceAnalysis | null;
  analysisStatus?: ImportedDoc["analysisStatus"];
  analysisError?: string | null;
  reviewDraft?: FactureDraft | null;
};

interface ImportedFactureReviewPanelProps {
  selectedDoc: ImportedInboxItem;
  onCreated: () => void;
  onClose: () => void;
  onAnalysisStart?: () => void;
}

const formatImportedAt = (date: Date) => {
  return DateTime.fromJSDate(date).toFormat("dd/MM/yyyy HH:mm");
};

const isInvoiceType = (value: string): value is InvoiceType => {
  return value === "normal" || value === "resto";
};

const isInvoiceMode = (value: string): value is InvoiceMode => {
  return modes.some((mode) => mode === value);
};

const ImportedFactureReviewPanel = ({
  selectedDoc,
  onCreated,
  onClose,
  onAnalysisStart,
}: ImportedFactureReviewPanelProps) => {
  const utils = trpc.useUtils();
  const { data: libellés } = trpc.libellé.getAll.useQuery();
  const analyzeMutation = trpc.facture.analyzeImportedDoc.useMutation();
  const createDraftMutation =
    trpc.facture.createDraftFromImportedDoc.useMutation({
      onSuccess: async () => {
        await Promise.all([
          utils.facture.invalidate(),
          utils.libellé.invalidate(),
        ]);
        onCreated();
      },
    });
  const commitMutation = trpc.facture.commitImportedBatch.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.facture.invalidate(),
        utils.libellé.invalidate(),
      ]);
      onCreated();
    },
  });
  const finalizeReviewMutation = trpc.facture.finalizeReviewedDraft.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.facture.invalidate(), utils.libellé.invalidate()]);
      onCreated();
    },
  });
  const rejectMutation = trpc.facture.rejectImportedDoc.useMutation({
    onSuccess: async () => {
      await utils.facture.invalidate();
      onClose();
    },
  });

  const initialValues = getImportedInvoiceReviewInitialValues(
    selectedDoc.analysis,
    selectedDoc.reviewDraft,
  );
  const [savedAnalysis, setSavedAnalysis] = React.useState(initialValues.analysis);
  const [showAnalyzeConfirmation, setShowAnalyzeConfirmation] =
    React.useState(false);
  const [emetteurSearch, setEmetteurSearch] = React.useState("");
  const [emetteur, setEmetteur] = React.useState(initialValues.emetteur);
  const [invoiceType, setInvoiceType] = React.useState<InvoiceType>(
    initialValues.invoiceType,
  );
  const [modeSearch, setModeSearch] = React.useState("");
  const [mode, setMode] = React.useState<InvoiceMode>(selectedDoc.reviewDraft?.reviewMode || "Prélèvement1");
  const [todo, setTodo] = React.useState(selectedDoc.invoiceToPay || false);
  const [compteSearch, setCompteSearch] = React.useState("");
  const [compte, setCompte] = React.useState(
    selectedDoc.reviewDraft?.restoredAccountingData?.compte || selectedDoc.reviewDraft?.reviewCompte || "",
  );
  const [modeRef, setModeRef] = React.useState(initialValues.modeRef);
  const [chequeNumber, setChequeNumber] = React.useState(selectedDoc.reviewDraft?.chequeNumber || "");
  const [montantCent, setMontantCent] = React.useState<number | null>(
    initialValues.montantCent,
  );
  const [dateFacture, setDateFacture] = React.useState<Date | null>(
    initialValues.dateFacture,
  );
  const [datePrelevement, setDatePrelevement] = React.useState<Date | null>(
    initialValues.datePrelevement,
  );
  const [paymentDueDate, setPaymentDueDate] = React.useState<Date | null>(
    initialValues.paymentDueDate,
  );
  const [montantTitresCent, setMontantTitresCent] = React.useState<
    number | null
  >(initialValues.montantTitresCent);
  const [montantFraisCent, setMontantFraisCent] = React.useState<number | null>(
    initialValues.montantFraisCent,
  );
  const [duplicateCandidates, setDuplicateCandidates] = React.useState<
    FactureDuplicateCandidate[]
  >([]);
  const [pendingAction, setPendingAction] = React.useState<
    "draft" | "commit" | null
  >(null);
  const [duplicateCheckError, setDuplicateCheckError] = React.useState<
    string | null
  >(null);
  const [pdfRefreshError, setPdfRefreshError] = React.useState<string | null>(
    null,
  );
  const [pdfUrl, setPdfUrl] = React.useState<string>(selectedDoc.pdfUrl);
  const isBusy =
    (!selectedDoc.reviewDraft && selectedDoc.analysisStatus === "analyzing") ||
    analyzeMutation.isPending ||
    createDraftMutation.isPending ||
    commitMutation.isPending ||
    finalizeReviewMutation.isPending ||
    rejectMutation.isPending;

  const refreshPdfUrl = async () => {
    setPdfRefreshError(null);
    try {
      const freshUrl = await utils.facture.refreshImportedDocPdfUrl.fetch({
        docId: selectedDoc._id,
      });
      setPdfUrl(freshUrl);
    } catch (caughtError) {
      setPdfRefreshError(
        caughtError instanceof Error
          ? caughtError.message
          : "Impossible de rafraîchir le PDF",
      );
    }
  };

  const emetteurCandidates = emetteurSearch
    ? (libellés || [])
        .map((libellé) => libellé.libellé)
        .filter((label) => new RegExp(emetteurSearch, "i").test(label))
    : [];

  const modeCandidates = modeSearch
    ? modes.filter((candidate) => new RegExp(modeSearch, "i").test(candidate))
    : [];

  const compteCandidates = filterCompte(compteSearch);

  const handleEmetteurChange = (value: string) => {
    const nextEmetteur = value || "";
    const matchingLibellé = libellés?.find(
      (libellé) => libellé.libellé === nextEmetteur,
    );
    setEmetteur(nextEmetteur);
    if (matchingLibellé) {
      setCompte(matchingLibellé.compte);
      setCompteSearch("");
    }
  };

  const applyAnalyzeResult = () => {
    onAnalysisStart?.();
    analyzeMutation.mutate(
      {
        docId: selectedDoc._id,
        invoiceType,
      },
      {
        onSuccess: async (result) => {
          if (result.status !== "done") {
            await utils.facture.invalidate();
            return;
          }
          setModeRef(result.modeRef || "");
          setInvoiceType(result.invoiceType);
          setMontantCent(result.montantCent);
          setDateFacture(result.dateFacture);
          setDatePrelevement(result.datePrelevement);
          setPaymentDueDate(result.paymentDueDate);
          setMontantTitresCent(result.montantTitresCent);
          setMontantFraisCent(result.montantFraisCent);
          setSavedAnalysis(result.analysis);
          await utils.facture.invalidate();
        },
        onError: async () => {
          await utils.facture.invalidate();
        },
      },
    );
  };

  const requestAnalysis = () => {
    if (
      shouldConfirmImportedInvoiceAnalysis({
        analysis: savedAnalysis,
        modeRef,
        montantCent,
        dateFacture,
        datePrelevement,
        paymentDueDate,
        montantTitresCent,
        montantFraisCent,
      })
    ) {
      setShowAnalyzeConfirmation(true);
    } else {
      applyAnalyzeResult();
    }
  };

  const blockingErrors: string[] = [];
  if (!emetteur.trim()) {
    blockingErrors.push("Émetteur obligatoire");
  }
  if (!modeRef.trim()) {
    blockingErrors.push("Référence facture obligatoire");
  }
  if (typeof montantCent !== "number") {
    blockingErrors.push("Montant TTC obligatoire");
  }
  if (!dateFacture && !datePrelevement) {
    blockingErrors.push("Date facture ou date prélèvement obligatoire");
  }
  if (!todo && invoiceType === "normal" && !selectedDoc.reviewDraft?.restoredAccountingData && !compte.trim()) {
    blockingErrors.push("Compte comptable obligatoire");
  }
  if (
    invoiceType === "resto" &&
    (typeof montantTitresCent !== "number" ||
      typeof montantFraisCent !== "number")
  ) {
    blockingErrors.push(
      "Montants titres/frais obligatoires pour une facture resto",
    );
  }

  const commitDraftOrFacture = (action: "draft" | "commit", confirmedDuplicateKeys: string[] = []) => {
    if (typeof montantCent !== "number") {
      return;
    }

    if (action === "draft") {
      createDraftMutation.mutate({
        docId: selectedDoc._id,
        emetteur: emetteur.trim(),
        invoiceType,
        modeRef: modeRef.trim(),
        chequeNumber: (chequeNumber || "").trim() || null,
        montantCent,
        dateFacture,
        datePrelevement,
        paymentDueDate,
        montantTitresCent,
        montantFraisCent,
        reviewMode: mode,
        reviewCompte: compte,
      });
      return;
    }

    if (selectedDoc.reviewDraft) {
      finalizeReviewMutation.mutate({
        draftId: selectedDoc.reviewDraft._id,
        libellé: emetteur.trim(), invoiceType, mode,
        compte: invoiceType === "normal" ? compte : null,
        modeRef: modeRef.trim(), chequeNumber: chequeNumber.trim() || null,
        montantCent, dateFacture, datePrelevement, montantTitresCent, montantFraisCent,
        confirmedDuplicateKeys,
        useRestoredAccountingData: !!selectedDoc.reviewDraft.restoredAccountingData,
      });
      return;
    }

    commitMutation.mutate({
      emetteur: emetteur.trim(),
      invoiceType,
      mode,
      compte: invoiceType === "normal" ? compte : null,
      items: [
        {
          docId: selectedDoc._id,
          originalFileName: selectedDoc.originalFileName,
          confirmedDuplicateKeys,
          modeRef: modeRef.trim(),
          chequeNumber:
            mode === "Chèque1"
              ? (chequeNumber || "").trim() || modeRef.trim()
              : null,
          montantCent: montantCent,
          dateFacture,
          datePrelevement,
          montantTitresCent,
          montantFraisCent,
        },
      ],
    });
  };

  const submit = async () => {
    if (blockingErrors.length > 0) {
      return;
    }

    if (typeof montantCent !== "number") {
      return;
    }

    const action = todo ? "draft" : "commit";
    setDuplicateCheckError(null);
    try {
      const duplicates = await utils.facture.checkBusinessDuplicates.fetch({
        modeRef: modeRef.trim(),
        date: dateFacture,
        datePrelevement,
        montantCent,
        excludeDraftId: selectedDoc.reviewDraft?._id,
      });
      if (duplicates.length > 0) {
        setDuplicateCandidates(duplicates);
        setPendingAction(action);
        return;
      }

      setPendingAction(action);
      commitDraftOrFacture(action);
    } catch (error) {
      setDuplicateCheckError(
        error instanceof Error
          ? error.message
          : "Erreur pendant la recherche de doublons",
      );
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center gap-3">
        <div>
          <div className="fw-bold">Traitement de la facture importée</div>
          <div className="text-muted small">
            {selectedDoc.originalFileName} • importée le{" "}
            {formatImportedAt(selectedDoc.uploadedAt)}
          </div>
          {selectedDoc.sourceEmailSender || selectedDoc.sourceEmailSubject ? (
            <div className="text-muted small">
              {selectedDoc.sourceEmailSender || "Expéditeur inconnu"}
              {selectedDoc.sourceEmailSubject
                ? ` • ${selectedDoc.sourceEmailSubject}`
                : ""}
            </div>
          ) : null}
        </div>
        <div className="d-flex gap-2">
          <a
            className="btn btn-outline-secondary btn-sm"
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
          >
            Voir le PDF
          </a>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => void refreshPdfUrl()}
          >
            Rafraîchir le PDF
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
          >
            Fermer
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="row g-3">
          <fieldset
            className="col-12 col-xl-7"
            disabled={isBusy || showAnalyzeConfirmation}
          >
            {!selectedDoc.reviewDraft && selectedDoc.analysisStatus === "failed" ? (
              <div className="alert alert-warning" role="alert">
                {selectedDoc.analysisError || "L'analyse OCR a échoué."}{" "}
                Vous pouvez réessayer avec le type choisi ou compléter les champs manuellement.
              </div>
            ) : null}
            {selectedDoc.reviewDraft ? (
              <div className="alert alert-info py-2">
                Les informations de votre dernier traitement sont conservées. Vous pouvez les corriger avant validation.
              </div>
            ) : savedAnalysis ? (
              <div className="alert alert-info py-2">
                Analyse OCR enregistrée le{" "}
                {formatImportedAt(savedAnalysis.analyzedAt)}. Les champs sont
                préremplis ; vous pouvez les corriger avant validation.
              </div>
            ) : null}
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={invoiceType}
                  onChange={(event) => {
                    const nextValue = event.currentTarget.value;
                    if (isInvoiceType(nextValue)) {
                      setInvoiceType(nextValue);
                    }
                  }}
                >
                  <option value="normal">Facture normale</option>
                  <option value="resto">Facture resto</option>
                </select>
              </div>
              {!todo ? (
                <div className="col-12 col-md-4">
                  <label className="form-label">Mode</label>
                  <AutoComplete
                    candidates={modeCandidates}
                    onSetSearch={(value) => setModeSearch(value || "")}
                    onSetValue={(value) => {
                      if (isInvoiceMode(value)) {
                        setMode(value);
                      }
                    }}
                    value={mode}
                    tagsOn={false}
                    acceptNewValues={false}
                    isValid={!!mode}
                    invalidMessage="Mode obligatoire"
                    isTouched={true}
                  />
                </div>
              ) : null}
              <div className="col-12 col-md-4 d-flex align-items-end">
                {selectedDoc.reviewDraft ? <span className="text-muted small">Informations déjà renseignées conservées. Vous pouvez les corriger ci-dessous.</span> : <button
                  type="button"
                  className={
                    todo
                      ? "btn btn-outline-primary w-100"
                      : "btn btn-primary w-100"
                  }
                  onClick={requestAnalysis}
                  disabled={analyzeMutation.isPending}
                >
                  {analyzeMutation.isPending
                    ? "Analyse OCR..."
                    : savedAnalysis
                      ? "Relancer l'analyse"
                      : "Analyser OCR"}
                </button>}
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">Émetteur</label>
                <AutoComplete
                  candidates={emetteurCandidates}
                  onSetSearch={(value) => setEmetteurSearch(value || "")}
                  onSetValue={handleEmetteurChange}
                  value={emetteur}
                  tagsOn={false}
                  acceptNewValues={true}
                  isValid={!!emetteur.trim()}
                  invalidMessage="Émetteur obligatoire"
                  isTouched={true}
                />
              </div>
              {!todo ? (
                <div className="col-12 col-md-6">
                  <label className="form-label">Compte</label>
                  <AutoComplete
                    candidates={compteCandidates}
                    onSetSearch={(value) => setCompteSearch(value || "")}
                    onSetValue={(value) => setCompte(value || "")}
                    value={compte}
                    tagsOn={false}
                    acceptNewValues={false}
                    isValid={invoiceType === "resto" || !!compte.trim()}
                    invalidMessage="Compte obligatoire pour une facture normale"
                    isTouched={true}
                  />
                </div>
              ) : null}

              <div className="col-12 col-md-4">
                <label className="form-label">Montant TTC</label>
                <FloatInput
                  valueCent={montantCent}
                  onValueCentChange={setMontantCent}
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Date facture</label>
                <DateInput date={dateFacture} onDateChange={setDateFacture} />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Date prélèvement</label>
                <DateInput
                  date={datePrelevement}
                  onDateChange={setDatePrelevement}
                />
              </div>
              {todo || selectedDoc.reviewDraft ? (
                <div className="col-12 col-md-4">
                  <label className="form-label">Échéance</label>
                  <DateInput
                    date={paymentDueDate}
                    onDateChange={setPaymentDueDate}
                  />
                </div>
              ) : null}

              <div className="col-12 col-md-6">
                <label className="form-label">Référence facture</label>
                <input
                  type="text"
                  className="form-control"
                  value={modeRef}
                  onChange={(event) => setModeRef(event.currentTarget.value)}
                />
              </div>
              {!todo && mode === "Chèque1" ? (
                <div className="col-12 col-md-6">
                  <label className="form-label">Numéro de chèque</label>
                  <input
                    type="text"
                    className="form-control"
                    value={chequeNumber}
                    onChange={(event) =>
                      setChequeNumber(event.currentTarget.value)
                    }
                  />
                </div>
              ) : null}

              {invoiceType === "resto" ? (
                <>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Montant titres</label>
                    <FloatInput
                      valueCent={montantTitresCent}
                      onValueCentChange={setMontantTitresCent}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Montant frais</label>
                    <FloatInput
                      valueCent={montantFraisCent}
                      onValueCentChange={setMontantFraisCent}
                    />
                  </div>
                </>
              ) : null}

              <div className="col-12">
                <div className="form-check">
                  <input
                    id="review-facture-todo"
                    type="checkbox"
                    className="form-check-input"
                    checked={todo}
                    onChange={(event) => setTodo(event.currentTarget.checked)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="review-facture-todo"
                  >
                    À régler
                  </label>
                </div>
              </div>
            </div>

            {analyzeMutation.error ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {analyzeMutation.error.message}
              </div>
            ) : null}
            {duplicateCheckError ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {duplicateCheckError}
              </div>
            ) : null}
            {createDraftMutation.error ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {createDraftMutation.error.message}
              </div>
            ) : null}
            {commitMutation.error ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {commitMutation.error.message}
              </div>
            ) : null}
            {finalizeReviewMutation.error ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">{finalizeReviewMutation.error.message}</div>
            ) : null}
            {rejectMutation.error ? (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {rejectMutation.error.message}
              </div>
            ) : null}
            {blockingErrors.length > 0 ? (
              <div className="alert alert-warning mt-3 mb-0" role="alert">
                {blockingErrors.join(" • ")}
              </div>
            ) : null}

            <div className="d-flex gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={
                  createDraftMutation.isPending ||
                  commitMutation.isPending ||
                  rejectMutation.isPending
                }
                onClick={() => rejectMutation.mutate(selectedDoc._id)}
              >
                {rejectMutation.isPending ? "Rejet..." : "Rejeter"}
              </button>
              <button
                type="button"
                className="btn btn-success"
                disabled={
                  blockingErrors.length > 0 ||
                  commitMutation.isPending ||
                  createDraftMutation.isPending ||
                  rejectMutation.isPending
                }
                onClick={submit}
              >
                {commitMutation.isPending || createDraftMutation.isPending || finalizeReviewMutation.isPending
                  ? "Enregistrement..."
                  : todo
                    ? "Ajouter aux règlements en attente"
                    : "Créer la facture"}
              </button>
            </div>
          </fieldset>

          <div className="col-12 col-xl-5">
            {pdfRefreshError ? (
              <div className="alert alert-danger py-2 small">
                {pdfRefreshError}
              </div>
            ) : null}
            <div className="ratio ratio-4x3 border rounded overflow-hidden">
              <iframe
                title={`pdf-${selectedDoc._id}`}
                src={pdfUrl}
              />
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        show={showAnalyzeConfirmation}
        title="Relancer l'analyse OCR ?"
        onCancel={() => setShowAnalyzeConfirmation(false)}
        onConfirm={() => {
          setShowAnalyzeConfirmation(false);
          applyAnalyzeResult();
        }}
        confirmLabel="Relancer et remplacer les champs"
        submittingLabel="Analyse OCR..."
        isSubmitting={analyzeMutation.isPending}
      >
        La nouvelle analyse remplacera le type, la référence, les montants et les
        dates du formulaire, y compris vos corrections non validées. Si elle
        échoue, les champs actuels et l'analyse précédente seront conservés.
      </ConfirmModal>
      <DuplicateFactureWarningModal
        show={duplicateCandidates.length > 0 && !!pendingAction}
        items={
          duplicateCandidates.length > 0
            ? [
                {
                  title: selectedDoc.originalFileName,
                  duplicates: duplicateCandidates,
                },
              ]
            : []
        }
        onCancel={() => {
          setDuplicateCandidates([]);
          setPendingAction(null);
        }}
        onConfirm={() => {
          if (!pendingAction || pendingAction === "draft") {
            return;
          }
          commitDraftOrFacture(pendingAction, getDuplicateConfirmationKeys(duplicateCandidates));
          setDuplicateCandidates([]);
          setPendingAction(null);
        }}
        confirmLabel="Créer la facture quand même"
        isSubmitting={createDraftMutation.isPending || commitMutation.isPending}
        blocking={pendingAction === "draft"}
      />
    </div>
  );
};

export default ImportedFactureReviewPanel;
